import { useState } from 'react';
import { toast } from 'sonner';
import { AudioLines, Loader2, Save, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { errMsg, mtvLyrics, mtvSettings, patchProject } from '../../../lib/aiStudioApi';
import { mmss } from './mtvSteps';

const box = 'rounded-xl border border-neutral-800 bg-neutral-900/40 p-5';
const Chip = ({ children, testId }) => <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] text-neutral-300" data-testid={testId}>{children}</span>;

function TimedLyrics({ project, lines, onChanged }) {
  const [rows, setRows] = useState(lines);
  const dirty = JSON.stringify(rows) !== JSON.stringify(lines);
  const save = () => mtvSettings(project.id, { lines: rows.filter((r) => r.text.trim()) })
    .then(() => { toast.success('Lyrics saved'); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-2">
      <div className="max-h-72 space-y-1 overflow-y-auto pr-1" data-testid="mtv-timed-lyrics">
        {rows.map((ln, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`mtv-lyric-line-${i}`}>
            <span className="w-10 shrink-0 font-mono text-[10px] text-emerald-300/80">{mmss(ln.start)}</span>
            <Input value={ln.text} onChange={(e) => setRows(rows.map((x, k) => (k === i ? { ...x, text: e.target.value } : x)))}
              className="h-7 border-transparent bg-transparent text-xs text-neutral-200 focus:border-neutral-700" data-testid={`mtv-lyric-text-${i}`} />
            <button type="button" onClick={() => setRows(rows.filter((_, k) => k !== i))} className="text-neutral-600 hover:text-red-300" data-testid={`mtv-lyric-delete-${i}`}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
      {dirty && <Button size="sm" onClick={save} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="mtv-lyrics-save-btn"><Save size={13} className="mr-1" /> Save lyrics</Button>}
    </div>
  );
}

/** Timed lyrics + beat/section detection, free first. The lyrics are the main source of the story. */
export function LyricsCard({ project, onChanged }) {
  const mtv = project.mtv || {};
  const a = mtv.analysis || {};
  const job = project.mtv_job || {};
  const running = job.status === 'running';
  const lines = mtv.lines || [];
  const [lyrics, setLyrics] = useState(project.lyrics || '');
  const listen = () => mtvLyrics(project.id).then(() => { toast.success('Listening to the song — about a minute'); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className={box} data-testid="mtv-lyrics-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-100"><AudioLines size={15} className="text-amber-300" /> Lyrics & timing</div>
      <p className="mb-3 text-xs text-neutral-400">Lyrics are written out automatically with a time for every line, plus the beat and the sections (intro, verse, chorus, bridge, outro). Edit anything — the story follows these lines.</p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={running || !project.music?.url} onClick={listen} className="border-neutral-700 text-neutral-200" data-testid="mtv-auto-lyrics-btn">
          {running && /Listening/.test(job.step || '') ? <Loader2 size={13} className="mr-1 animate-spin" /> : <AudioLines size={13} className="mr-1" />}
          {lines.length ? 'Listen again' : 'Get lyrics automatically'} · <span className="ml-1 text-emerald-300">free</span>
        </Button>
        {a.bpm ? <Chip testId="mtv-bpm">{Math.round(a.bpm)} BPM</Chip> : null}
        {a.sections?.length ? <Chip testId="mtv-sections-count">{a.sections.length} sections</Chip> : null}
        {lines.length ? <Chip testId="mtv-lines-count">{lines.length} lines</Chip> : null}
        {mtv.language ? <Chip testId="mtv-language">{mtv.language}</Chip> : null}
      </div>
      <div className="mb-2 text-[11px] text-neutral-400" data-testid="mtv-lyrics-status">
        {!/Listening|lyric lines/.test(job.step || '') ? '' : running ? job.step : job.status === 'failed' ? <span className="text-red-300">{job.error}</span> : ''}
      </div>
      {lines.length ? <TimedLyrics key={`${lines.length}-${lines[0]?.start}-${lines[lines.length - 1]?.end}`} project={project} lines={lines} onChanged={onChanged} /> : (
        <Textarea rows={6} value={lyrics} onChange={(e) => setLyrics(e.target.value)} placeholder="Or paste the lyrics here…" className="border-neutral-700 bg-neutral-950 text-xs" data-testid="mtv-lyrics"
          onBlur={() => lyrics !== (project.lyrics || '') && patchProject(project.id, { lyrics }).then(() => { toast.success('Lyrics saved'); onChanged(); }).catch((e) => toast.error(errMsg(e)))} />
      )}
    </div>
  );
}
